import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { nameSearchAPI } from '../../utils/api';
import {
  FaHome,
  FaBook,
  FaUserTie,
  FaUser,
  FaMagic,
  FaMoon,
  FaUsers,
  FaLink,
  FaBalanceScale,
  FaQuestionCircle,
  FaHeart,
  FaEnvelope,
  FaFileContract,
  FaShieldAlt,
  FaInfoCircle,
  FaUndo
} from 'react-icons/fa';
import './Footer.css';

/**
 * Footer
 * - Mobil (<=768px): Uygulama tarzı alt navigasyon (Home, Favorites, E-Books, Profile)
 * - Masaüstü (>768px): Sistem temasıyla uyumlu klasik site footer (brand, nav, tools, legal, copyright)
 */
const Footer = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );
  const [favCount, setFavCount] = useState(0);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Alt navigasyon badge'i: favori sayısı
  useEffect(() => {
    let cancelled = false;
    if (isAuthenticated && user?.id) {
      nameSearchAPI.getUserFavorites(user.id)
        .then((res) => {
          if (cancelled || !res?.success) return;
          const list = res.data?.data || res.data || [];
          setFavCount(Array.isArray(list) ? list.length : 0);
        })
        .catch(() => {});
    } else {
      setFavCount(0);
    }
    return () => { cancelled = true; };
  }, [isAuthenticated, user?.id]);

  // ============ MOBILE: Bottom Tab Nav ============
  if (isMobile) {
    const isProfile = location.pathname === '/profile' && !location.search.includes('tab=appointments');

    const items = [
      {
        to: '/',
        end: true,
        active: location.pathname === '/',
        icon: <FaHome aria-hidden="true" />,
        label: t('common.navigation.home')
      },
      {
        to: '/favorites',
        active: location.pathname.startsWith('/favorites'),
        badge: favCount,
        icon: <FaHeart aria-hidden="true" />,
        label: t('common.navigation.favorites')
      },
      {
        to: '/my-ebooks',
        active: location.pathname.startsWith('/my-ebooks'),
        icon: <FaBook aria-hidden="true" />,
        label: t('common.navigation.myEbooks')
      },
      {
        to: '/profile',
        active: isProfile,
        icon: <FaUser aria-hidden="true" />,
        label: t('common.navigation.profile')
      }
    ];

    return (
      <nav className="mobile-bottom-nav" aria-label="Primary">
        {items.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className={`mobile-bottom-nav-item${item.active ? ' active' : ''}`}
            aria-current={item.active ? 'page' : undefined}
          >
            <span className="mobile-bottom-nav-icon">
              {item.icon}
              {item.badge > 0 && <span className="mobile-bottom-nav-badge">{item.badge > 99 ? '99+' : item.badge}</span>}
            </span>
            <span className="mobile-bottom-nav-label">{item.label}</span>
          </Link>
        ))}
      </nav>
    );
  }

  // ============ DESKTOP: Site Footer ============
  const currentYear = new Date().getFullYear();

  return (
    <footer className="site-footer" role="contentinfo">
      <div className="site-footer-inner">
        {/* Brand */}
        <div className="site-footer-brand">
          <Link to="/" className="site-footer-logo" aria-label="BabynamenIman">
            <img src="/BNI.png" alt="BabynamenIman" className="site-footer-logo-image" />
          </Link>
          <p className="site-footer-tagline">
            {t('footer.forFamilies', 'for Muslim families worldwide')}
          </p>
        </div>

        {/* Navigation */}
        <div className="site-footer-column">
          <h4 className="site-footer-title">
            {t('footer.navigation.title', 'Navigation')}
          </h4>
          <ul className="site-footer-links">
            <li>
              <Link to="/">
                <FaHome className="site-footer-link-icon" aria-hidden="true" />
                <span>{t('common.navigation.home')}</span>
              </Link>
            </li>
            <li>
              <Link to="/?open=library">
                <FaBook className="site-footer-link-icon" aria-hidden="true" />
                <span>{t('common.navigation.shop')}</span>
              </Link>
            </li>
            <li>
              <Link to="/?open=consult">
                <FaUserTie className="site-footer-link-icon" aria-hidden="true" />
                <span>{t('common.navigation.consulting')}</span>
              </Link>
            </li>
            <li>
              <Link to="/favorites">
                <FaHeart className="site-footer-link-icon" aria-hidden="true" />
                <span>{t('common.navigation.favorites', 'Favorites')}</span>
              </Link>
            </li>
          </ul>
        </div>

        {/* Tools — tüm araçlar ana sayfa panelinde açılır (/?open=<arac>) */}
        <div className="site-footer-column">
          <h4 className="site-footer-title">
            {t('header.tools', 'Tools')}
          </h4>
          <ul className="site-footer-links">
            <li>
              <Link to="/?open=meaning">
                <FaQuestionCircle className="site-footer-link-icon" aria-hidden="true" />
                <span>{t('common.navigation.nameMeaning')}</span>
              </Link>
            </li>
            <li>
              <Link to="/?open=suggestion">
                <FaMagic className="site-footer-link-icon" aria-hidden="true" />
                <span>{t('common.navigation.nameSuggestion')}</span>
              </Link>
            </li>
            <li>
              <Link to="/?open=analysis">
                <FaMoon className="site-footer-link-icon" aria-hidden="true" />
                <span>{t('common.navigation.islamicAnalysis')}</span>
              </Link>
            </li>
            <li>
              <Link to="/?open=match">
                <FaUsers className="site-footer-link-icon" aria-hidden="true" />
                <span>{t('common.navigation.nameMatch')}</span>
              </Link>
            </li>
            <li>
              <Link to="/?open=harmony">
                <FaLink className="site-footer-link-icon" aria-hidden="true" />
                <span>{t('common.navigation.nameHarmony')}</span>
              </Link>
            </li>
            <li>
              <Link to="/?open=compare">
                <FaBalanceScale className="site-footer-link-icon" aria-hidden="true" />
                <span>{t('common.navigation.nameCompare')}</span>
              </Link>
            </li>
          </ul>
        </div>

        {/* Legal */}
        <div className="site-footer-column">
          <h4 className="site-footer-title">
            {t('footer.legal.title', 'Legal')}
          </h4>
          <ul className="site-footer-links">
            <li>
              <Link to="/terms">
                <FaFileContract className="site-footer-link-icon" aria-hidden="true" />
                <span>{t('footer.legal.terms', 'Terms of Service')}</span>
              </Link>
            </li>
            <li>
              <Link to="/privacy">
                <FaShieldAlt className="site-footer-link-icon" aria-hidden="true" />
                <span>{t('footer.legal.privacy', 'Privacy Policy')}</span>
              </Link>
            </li>
            <li>
              <Link to="/impressum">
                <FaInfoCircle className="site-footer-link-icon" aria-hidden="true" />
                <span>{t('footer.legal.impressum', 'Imprint')}</span>
              </Link>
            </li>
            <li>
              <Link to="/returns">
                <FaUndo className="site-footer-link-icon" aria-hidden="true" />
                <span>{t('footer.legal.returns', 'Returns & Refunds')}</span>
              </Link>
            </li>
            <li>
              <Link to="/contact">
                <FaEnvelope className="site-footer-link-icon" aria-hidden="true" />
                <span>{t('footer.legal.contact', 'Contact')}</span>
              </Link>
            </li>
          </ul>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="site-footer-bottom">
        <div className="site-footer-bottom-inner">
          <p className="site-footer-copy">
            © {currentYear} BabynamenIman.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
